import { createUserFacingError, formatUserFacingError, withErrorCode } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { adminBillingService, type BillingSettings } from '../services/admin.billing.service'
import {
  createStripeConnectOnboardingLinkFn,
  getStripeConnectStatusFn,
  type StripeConnectStatus,
} from '@/lib/firebase/functions'

function ReadinessRow({ label, ready }: { label: string; ready: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b py-2 last:border-b-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <Badge variant={ready ? 'default' : 'secondary'}>{ready ? 'Ready' : 'Needs attention'}</Badge>
    </div>
  )
}

type BillingForm = Record<
  | 'depositThreshold'
  | 'depositAutoCancelDays'
  | 'depositProviderBufferHours'
  | 'depositFirstReminderHoursBefore'
  | 'depositFinalReminderHoursBefore',
  string
>

function NumberField({
  id,
  label,
  helperText,
  value,
  suffix,
  onChange,
}: {
  id: string
  label: string
  helperText: string
  value: string
  suffix: string
  onChange: (value: string) => void
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          type="number"
          min="0"
          step="1"
          className="w-36"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        <span className="text-sm text-muted-foreground">{suffix}</span>
      </div>
      <p className="text-xs text-muted-foreground">{helperText}</p>
    </div>
  )
}

function toBillingForm(settings: BillingSettings): BillingForm {
  return {
    depositThreshold: String(settings.depositThreshold),
    depositAutoCancelDays: String(settings.depositAutoCancelDays),
    depositProviderBufferHours: String(settings.depositProviderBufferHours),
    depositFirstReminderHoursBefore: String(settings.depositFirstReminderHoursBefore),
    depositFinalReminderHoursBefore: String(settings.depositFinalReminderHoursBefore),
  }
}

export function AdminStripeSettingsPage() {
  const [status, setStatus] = useState<StripeConnectStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [openingOnboarding, setOpeningOnboarding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [billing, setBilling] = useState<BillingSettings | null>(null)
  const [billingForm, setBillingForm] = useState<BillingForm | null>(null)
  const [billingError, setBillingError] = useState<string | null>(null)
  const [billingSaved, setBillingSaved] = useState(false)
  const [savingBilling, setSavingBilling] = useState(false)

  const loadStatus = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await getStripeConnectStatusFn({})
      setStatus(result.data)
    } catch (loadError) {
      setStatus(null)
      console.error('Unable to load Stripe Connect status:', loadError)
      setError(withErrorCode('We could not refresh Stripe right now. Try again in a moment; your saved billing policy is still available below.', 'app/validation'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadStatus()
    void adminBillingService.getBillingSettings()
      .then((settings) => {
        setBilling(settings)
        setBillingForm(toBillingForm(settings))
      })
      .catch((loadError: unknown) => {
        setBillingError(formatUserFacingError(loadError, 'Unable to load billing policy.'))
      })
  }, [loadStatus])

  const billingDirty = billing !== null && billingForm !== null &&
    (Object.keys(billingForm) as Array<keyof BillingForm>)
      .some((key) => billingForm[key] !== String(billing[key]))

  const saveBilling = async () => {
    if (!billingForm) return
    setSavingBilling(true)
    setBillingError(null)
    setBillingSaved(false)
    try {
      const parsed = Object.fromEntries(
        Object.entries(billingForm).map(([key, value]) => [key, Number(value)])
      ) as Pick<
        BillingSettings,
        | 'depositThreshold'
        | 'depositAutoCancelDays'
        | 'depositProviderBufferHours'
        | 'depositFirstReminderHoursBefore'
        | 'depositFinalReminderHoursBefore'
      >
      for (const [key, value] of Object.entries(parsed)) {
        if (!Number.isFinite(value) || value < 0) {
          throw createUserFacingError(`${key} must be a non-negative number.`)
        }
      }
      if (parsed.depositAutoCancelDays < 1) {
        throw createUserFacingError('The auto-cancel window must be at least one day.')
      }
      if (parsed.depositFinalReminderHoursBefore < 1) {
        throw createUserFacingError('The final reminder must be at least one hour before cancellation.')
      }
      if (parsed.depositFirstReminderHoursBefore <= parsed.depositFinalReminderHoursBefore) {
        throw createUserFacingError('The first reminder must be scheduled before the final reminder.')
      }

      await adminBillingService.updateBillingSettings(parsed)
      const nextBilling = { ...(billing ?? parsed), ...parsed }
      setBilling(nextBilling)
      setBillingForm(toBillingForm(nextBilling))
      setBillingSaved(true)
    } catch (saveError) {
      setBillingError(formatUserFacingError(saveError, 'Unable to save billing policy.'))
    } finally {
      setSavingBilling(false)
    }
  }

  const continueOnboarding = async () => {
    setOpeningOnboarding(true)
    setError(null)
    try {
      const result = await createStripeConnectOnboardingLinkFn({})
      window.location.assign(result.data.url)
    } catch (onboardingError) {
      setError(
        formatUserFacingError(onboardingError, 'Unable to open Stripe onboarding.')
      )
      setOpeningOnboarding(false)
    }
  }

  return (
    <div className="max-w-3xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold">Stripe Billing</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          One Day Dance client invoices are issued through the connected Stripe account.
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Stripe connection unavailable</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>Connected account</CardTitle>
              <CardDescription>
                Managed by the Unnamed Development Stripe Connect platform.
              </CardDescription>
            </div>
            {status && (
              <Badge variant={status.readyForClientBilling ? 'default' : 'secondary'}>
                {status.readyForClientBilling ? 'Billing ready' : 'Setup required'}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <p className="text-sm text-muted-foreground">Checking Stripe…</p>
          ) : status ? (
            <>
              <div className="rounded-md border px-4">
                <ReadinessRow label="Business details submitted" ready={status.detailsSubmitted} />
                <ReadinessRow label="Client charges enabled" ready={status.chargesEnabled} />
                <ReadinessRow label="Payouts enabled" ready={status.payoutsEnabled} />
              </div>

              <dl className="grid gap-2 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-muted-foreground">Business</dt>
                  <dd className="font-medium">{status.businessName ?? 'Not provided'}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Stripe account</dt>
                  <dd className="font-mono text-xs">{status.accountId}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Country / currency</dt>
                  <dd className="font-medium">
                    {[status.country, status.defaultCurrency?.toUpperCase()].filter(Boolean).join(' / ') || '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Requirements</dt>
                  <dd className="font-medium">
                    {status.pastDueCount > 0
                      ? `${status.pastDueCount} past due`
                      : `${status.currentlyDueCount} currently due`}
                  </dd>
                </div>
              </dl>

              {status.disabledReason && (
                <Alert variant="destructive">
                  <AlertTitle>Stripe restricted this account</AlertTitle>
                  <AlertDescription>
                    {withErrorCode(
                      'Stripe has restricted this account. Review the requirements above and try again.',
                      'billing/stripe-account-restricted',
                    )}
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex flex-wrap gap-2">
                {!status.readyForClientBilling && (
                  <Button disabled={openingOnboarding} onClick={() => void continueOnboarding()}>
                    {openingOnboarding ? 'Opening Stripe…' : 'Continue Stripe setup'}
                  </Button>
                )}
                <Button variant="outline" disabled={loading} onClick={() => void loadStatus()}>
                  Refresh status
                </Button>
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Client invoice workflow</CardTitle>
          <CardDescription>
            Estimates are approved on the site; invoices and payments are handled securely by Stripe.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-md border p-3">
              <p className="font-medium">1. Client accepts</p>
              <p className="mt-1 text-xs text-muted-foreground">A secure estimate link records approval.</p>
            </div>
            <div className="rounded-md border p-3">
              <p className="font-medium">2. Deposit confirms</p>
              <p className="mt-1 text-xs text-muted-foreground">Larger jobs are confirmed after the Stripe deposit is paid.</p>
            </div>
            <div className="rounded-md border p-3">
              <p className="font-medium">3. Final balance</p>
              <p className="mt-1 text-xs text-muted-foreground">After delivery, you review the itemized balance and confirm the send.</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Stripe invoices are due in seven days. Payment events update the engagement automatically; the engagement panel also includes a reconciliation action if a webhook is delayed.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Billing policy</CardTitle>
          <CardDescription>
            Control when deposits are required and how unpaid deposit reminders are scheduled.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!billingForm ? (
            <p className="text-sm text-muted-foreground">Loading billing policy…</p>
          ) : (
            <div className="space-y-5">
              <NumberField
                id="deposit-threshold"
                label="Deposit threshold"
                suffix="USD"
                helperText="Jobs above this total require a 50% deposit. Jobs at or below it are invoiced in full after the service."
                value={billingForm.depositThreshold}
                onChange={(value) => setBillingForm({ ...billingForm, depositThreshold: value })}
              />
              <Separator />
              <div className="grid gap-5 sm:grid-cols-2">
                <NumberField
                  id="deposit-auto-cancel-days"
                  label="Maximum unpaid window"
                  suffix="days"
                  helperText="Maximum time before an unpaid booking is released."
                  value={billingForm.depositAutoCancelDays}
                  onChange={(value) => setBillingForm({ ...billingForm, depositAutoCancelDays: value })}
                />
                <NumberField
                  id="deposit-provider-buffer"
                  label="Provider notice buffer"
                  suffix="hours"
                  helperText="An unpaid booking is released no later than this many hours before service."
                  value={billingForm.depositProviderBufferHours}
                  onChange={(value) => setBillingForm({ ...billingForm, depositProviderBufferHours: value })}
                />
                <NumberField
                  id="deposit-first-reminder"
                  label="First reminder"
                  suffix="hours before release"
                  helperText="When Stripe's payment link is sent as a gentle reminder."
                  value={billingForm.depositFirstReminderHoursBefore}
                  onChange={(value) => setBillingForm({ ...billingForm, depositFirstReminderHoursBefore: value })}
                />
                <NumberField
                  id="deposit-final-reminder"
                  label="Final reminder"
                  suffix="hours before release"
                  helperText="When the client and admins receive the final notice."
                  value={billingForm.depositFinalReminderHoursBefore}
                  onChange={(value) => setBillingForm({ ...billingForm, depositFinalReminderHoursBefore: value })}
                />
              </div>
              <div className="flex flex-wrap items-center gap-3 pt-1">
                <Button disabled={!billingDirty || savingBilling} onClick={() => void saveBilling()}>
                  {savingBilling ? 'Saving…' : 'Save Billing Policy'}
                </Button>
                {billingSaved && <span className="text-sm text-emerald-600">Billing policy saved.</span>}
                {billingError && <span className="text-sm text-destructive">{billingError}</span>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
